import { relations, sql } from "drizzle-orm";
import { boolean, integer, jsonb, pgEnum, pgTable, primaryKey, serial, text, timestamp, unique, uuid, } from "drizzle-orm/pg-core";
import { dataRequestTable } from "./dataRequest.js";
import { dossier, dossierDocument } from "./dossier.js";
import { loanOfferTable } from "./loanOffers.js";
import { agency, agencyServiceProviderConnection, appUser } from "./main.js";
import { workItem } from "./workItems.js";
export const hdnDossierTable = pgTable("hdn_dossier", {
    status: text("status"),
    statusMessage: text("status_message"),
    statusTimestamp: timestamp("status_timestamp", {
        withTimezone: true,
        mode: "string",
    }),
    kind: text("kind").notNull().$type(),
    providerCode: text("provider_code").references(() => hdnProvider.name, {
        onUpdate: "cascade",
    }),
    receiverCode: text("receiver_code")
        .notNull()
        .references(() => hdnReceiver.receiverCode, { onUpdate: "cascade" })
        .$type(),
    serviceProviderId: uuid("service_provider_id").references(() => hdnServiceProvider.id, { onUpdate: "cascade" }),
    createdAt: timestamp("created_at", {
        withTimezone: true,
        mode: "string",
    })
        .defaultNow()
        .notNull(),
    requestVersion: integer("request_version").notNull().default(1),
    hdnRequestTraceNr: text("hdn_request_trace_nr").notNull().unique(),
    hdnId: uuid("hdn_id"),
    dossierId: uuid("dossier_id")
        .notNull()
        .references(() => dossier.id, { onDelete: "cascade", onUpdate: "cascade" }),
    agencyId: uuid("agency_id")
        .notNull()
        .references(() => agency.id, { onDelete: "cascade", onUpdate: "cascade" }),
    id: uuid("id")
        .default(sql `uuid_generate_v4()`)
        .primaryKey()
        .notNull(),
});
export const hdnDossierRelations = relations(hdnDossierTable, ({ one, many }) => ({
    loanApplication: one(hdnLoanApplication, {
        fields: [hdnDossierTable.id],
        references: [hdnLoanApplication.hdnDossierId],
    }),
    informatieAanvraag: one(hdnInformatieAanvraagTable, {
        fields: [hdnDossierTable.id],
        references: [hdnInformatieAanvraagTable.hdnDossierId],
    }),
    hdnReceiver: one(hdnReceiver, {
        fields: [hdnDossierTable.receiverCode],
        references: [hdnReceiver.receiverCode],
    }),
    dataRequest: one(dataRequestTable, {
        fields: [hdnDossierTable.id],
        references: [dataRequestTable.hdnDossierId],
    }),
    messages: many(hdnMessage),
    agency: one(agency, {
        fields: [hdnDossierTable.agencyId],
        references: [agency.id],
    }),
    serviceProvider: one(hdnServiceProvider, {
        fields: [hdnDossierTable.serviceProviderId],
        references: [hdnServiceProvider.id],
    }),
    provider: one(hdnProvider, {
        fields: [hdnDossierTable.providerCode],
        references: [hdnProvider.name],
    }),
}));
export const hdnInformatieAanvraagTable = pgTable("hdn_informatie_aanvraag", {
    content: jsonb("content").notNull().$type(),
    response: jsonb("response").$type(),
    existingMortgageId: text("existing_mortgage_id"),
    hdnDossierId: uuid("hdn_dossier_id")
        .notNull()
        .references(() => hdnDossierTable.id, {
        onDelete: "cascade",
        onUpdate: "cascade",
    }),
    dossierId: uuid("dossier_id")
        .notNull()
        .references(() => dossier.id, { onDelete: "cascade", onUpdate: "cascade" }),
    agencyId: uuid("agency_id")
        .notNull()
        .references(() => agency.id, { onDelete: "cascade", onUpdate: "cascade" }),
    createdAt: timestamp("created_at", {
        withTimezone: true,
        mode: "string",
    })
        .defaultNow()
        .notNull(),
    id: uuid("id")
        .default(sql `uuid_generate_v4()`)
        .primaryKey()
        .notNull(),
});
export const hdnInformatieAanvraagRelations = relations(hdnInformatieAanvraagTable, ({ many, one }) => ({
    hdnDossier: one(hdnDossierTable, {
        fields: [hdnInformatieAanvraagTable.hdnDossierId],
        references: [hdnDossierTable.id],
    }),
    dossier: one(dossier, {
        fields: [hdnInformatieAanvraagTable.dossierId],
        references: [dossier.id],
    }),
    agency: one(agency, {
        fields: [hdnInformatieAanvraagTable.agencyId],
        references: [agency.id],
    }),
    workItems: many(workItem),
}));
export const hdnLoanApplication = pgTable("hdn_loan_application", {
    createdAt: timestamp("created_at", {
        withTimezone: true,
        mode: "string",
    })
        .defaultNow()
        .notNull(),
    kenmerk: text("kenmerk"),
    hdnDossierId: uuid("hdn_dossier_id")
        .notNull()
        .references(() => hdnDossierTable.id, {
        onDelete: "cascade",
        onUpdate: "cascade",
    }),
    dossierId: uuid("dossier_id")
        .notNull()
        .references(() => dossier.id, { onDelete: "cascade", onUpdate: "cascade" }),
    agencyId: uuid("agency_id")
        .notNull()
        .references(() => agency.id, { onDelete: "cascade", onUpdate: "cascade" }),
    id: uuid("id")
        .default(sql `uuid_generate_v4()`)
        .primaryKey()
        .notNull(),
    // TODO: remove. Redundant, kept to keep the data (until we can verify everything still works)
    status: text("status"),
    statusMessage: text("status_message"),
    lenderCode: text("lender_code"),
    receiverCode: text("receiver_code"),
    serviceProviderId: uuid("service_provider_id"),
    hdnId: uuid("hdn_id"),
    currentLoanApplicationVersion: integer("current_loan_application_version").default(1),
});
export const hdnLoanApplicationRelations = relations(hdnLoanApplication, ({ many, one }) => ({
    messages: many(hdnMessage),
    versions: many(hdnLoanApplicationVersion),
    requestedDocuments: many(hdnRequestedDocument),
    workItems: many(workItem),
    hdnDossier: one(hdnDossierTable, {
        fields: [hdnLoanApplication.hdnDossierId],
        references: [hdnDossierTable.id],
    }),
    dossier: one(dossier, {
        fields: [hdnLoanApplication.dossierId],
        references: [dossier.id],
    }),
    agency: one(agency, {
        fields: [hdnLoanApplication.agencyId],
        references: [agency.id],
    }),
    loanOffers: many(loanOfferTable),
}));
export const hdnMessageSchema = pgTable("hdn_message_schema", {
    messageType: text("message_type").notNull(),
    receiverId: text("receiver_id").notNull(),
    schemaVersion: text("schema_version").notNull(),
    majorVersion: text("major_version")
        .generatedAlwaysAs(() => sql `substring(split_part(${hdnMessageSchema.schemaVersion}, '.', 1) || '.' || split_part(${hdnMessageSchema.schemaVersion}, '.', 2), 1)`)
        .notNull(),
    startingDate: timestamp("starting_date", {
        withTimezone: true,
        mode: "string",
    }).notNull(),
    xsd: text("xsd"),
    xsdJson: jsonb("xsd_json").$type(),
    xsdMapping: jsonb("xsd_mapping").$type(),
    controleXml: text("controle_xml"),
    shouldFetch: boolean("should_fetch").default(true).notNull(),
    createdAt: timestamp("created_at", {
        withTimezone: true,
        mode: "string",
    })
        .defaultNow()
        .notNull(),
    id: serial("id").primaryKey(),
}, (table) => [
    unique("hdn_message_schema_name_organisation_version_key").on(table.messageType, table.receiverId, table.schemaVersion),
]);
export const hdnMessageSchemaRelations = relations(hdnMessageSchema, ({ one, many }) => ({
    receiver: one(hdnReceiver, {
        fields: [hdnMessageSchema.receiverId],
        references: [hdnReceiver.receiverId],
    }),
    messages: many(hdnMessage),
}));
export const hdnLoanApplicationVersion = pgTable("hdn_loan_application_version", {
    content: jsonb("content").notNull().$type(),
    displayId: integer("display_id").notNull().default(1),
    schemaVersion: text("schema_version").notNull(),
    sentAt: timestamp("sent_at", {
        withTimezone: true,
        mode: "string",
    }),
    hdnLoanApplicationId: uuid("hdn_loan_application_id")
        .notNull()
        .references(() => hdnLoanApplication.id, {
        onDelete: "cascade",
        onUpdate: "cascade",
    }),
    agencyId: uuid("agency_id")
        .notNull()
        .references(() => agency.id, {
        onDelete: "cascade",
        onUpdate: "cascade",
    }),
    lastUpdatedAt: timestamp("last_updated_at", {
        withTimezone: true,
        mode: "string",
    }).defaultNow(),
    createdAt: timestamp("created_at", {
        withTimezone: true,
        mode: "string",
    })
        .defaultNow()
        .notNull(),
    id: uuid("id")
        .primaryKey()
        .notNull()
        .default(sql `uuid_generate_v4()`),
}, (hdnLoanApplicationVersion) => [
    unique("hdn_loan_application_unq").on(hdnLoanApplicationVersion.hdnLoanApplicationId, hdnLoanApplicationVersion.displayId),
]);
export const hdnLoanApplicationVersionRelations = relations(hdnLoanApplicationVersion, ({ one }) => ({
    loanApplication: one(hdnLoanApplication, {
        fields: [hdnLoanApplicationVersion.hdnLoanApplicationId],
        references: [hdnLoanApplication.id],
    }),
    agency: one(agency, {
        fields: [hdnLoanApplicationVersion.agencyId],
        references: [agency.id],
    }),
}));
export const hdnMessage = pgTable("hdn_message", {
    // What's the exact value of the field (SX StatusMelding, StatusMelding or SX)
    messageType: text("message_type").notNull(),
    messageVersion: text("message_version").notNull(),
    requestVersion: text("request_version").notNull(),
    contentXml: text("content_xml"),
    contentJson: jsonb("content_json"),
    contentType: text("content_type")
        .notNull()
        .default("XML")
        .$type(),
    hdnResourceId: text("hdn_resource_id").unique(),
    handledAt: timestamp("handled_at", {
        withTimezone: true,
        mode: "string",
    }),
    messageStatus: text("message_status").$type(),
    schemaId: integer("schema_id").references(() => hdnMessageSchema.id, {
        onUpdate: "cascade",
    }),
    hdnDossierId: uuid("hdn_dossier_id")
        .notNull()
        .references(() => hdnDossierTable.id, {
        onDelete: "cascade",
        onUpdate: "cascade",
    }),
    hdnLoanApplicationId: uuid("hdn_loan_application_id").references(() => hdnLoanApplication.id, {
        onDelete: "cascade",
        onUpdate: "cascade",
    }),
    agencyId: uuid("agency_id")
        .notNull()
        .references(() => agency.id, {
        onDelete: "cascade",
        onUpdate: "cascade",
    }),
    sentAt: timestamp("sent_at", {
        withTimezone: true,
        mode: "string",
    }).notNull(),
    createdAt: timestamp("created_at", {
        withTimezone: true,
        mode: "string",
    })
        .defaultNow()
        .notNull(),
    id: uuid("id")
        .default(sql `uuid_generate_v4()`)
        .primaryKey()
        .notNull(),
});
export const hdnMessageRelations = relations(hdnMessage, ({ one }) => ({
    schema: one(hdnMessageSchema, {
        fields: [hdnMessage.schemaId],
        references: [hdnMessageSchema.id],
    }),
    hdnDossier: one(hdnDossierTable, {
        fields: [hdnMessage.hdnDossierId],
        references: [hdnDossierTable.id],
    }),
    loanApplication: one(hdnLoanApplication, {
        fields: [hdnMessage.hdnLoanApplicationId],
        references: [hdnLoanApplication.id],
    }),
    agency: one(agency, {
        fields: [hdnMessage.agencyId],
        references: [agency.id],
    }),
}));
export const hdnNodeType = pgEnum("hdn_node_type", [
    "provider",
    "intermediary",
    "service_provider",
    "servicer",
    "software_intermediary",
    "mid_back_office",
    "data_source",
    "data_source_provider",
    "supply_chain_partner",
]);
export const hdnNode = pgTable("hdn_node", {
    nodeNumber: text("hdn_number").notNull().unique(),
    kvkNumber: text("kvk_number"),
    zipCode: text("zip_code"),
    nodeName: text("node_name"),
    uuidInHdn: text("uuid_in_hdn"),
    nodeType: hdnNodeType("node_type").notNull(),
    createdAt: timestamp("created_at", {
        withTimezone: true,
        mode: "string",
    })
        .defaultNow()
        .notNull(),
    id: uuid("id")
        .default(sql `uuid_generate_v4()`)
        .primaryKey()
        .notNull(),
});
export const hdnNodeRelations = relations(hdnNode, ({ many }) => ({
    agencies: many(agency),
    receiverNode: many(hdnReceiverNode),
}));
export const hdnReceiverNode = pgTable("hdn_receiver_node", {
    receiverCode: text("receiver_code")
        .notNull()
        .references(() => hdnReceiver.receiverCode, { onUpdate: "cascade" })
        .$type(),
    nodeNumber: text("node_number")
        .notNull()
        .references(() => hdnNode.nodeNumber, { onUpdate: "cascade" }),
    type: text("type").$type(),
    createdAt: timestamp("created_at", {
        withTimezone: true,
        mode: "string",
    })
        .defaultNow()
        .notNull(),
    id: uuid("id")
        .default(sql `uuid_generate_v4()`)
        .primaryKey()
        .notNull(),
});
export const hdnReceiverNodeRelations = relations(hdnReceiverNode, ({ one }) => ({
    receiver: one(hdnReceiver, {
        fields: [hdnReceiverNode.receiverCode],
        references: [hdnReceiver.receiverCode],
    }),
    hdnNode: one(hdnNode, {
        fields: [hdnReceiverNode.nodeNumber],
        references: [hdnNode.nodeNumber],
    }),
}));
export const hdnReceiver = pgTable("hdn_receiver", {
    receiverCode: text("receiver_code").notNull().unique().$type(),
    receiverId: text("receiver_id").notNull(),
    createdAt: timestamp("created_at", {
        withTimezone: true,
        mode: "string",
    })
        .defaultNow()
        .notNull(),
    id: uuid("id")
        .default(sql `uuid_generate_v4()`)
        .primaryKey()
        .notNull(),
});
export const hdnReceiverRelations = relations(hdnReceiver, ({ many }) => ({
    hdnReceiverNode: many(hdnReceiverNode),
    hdnProvider: many(hdnProvider),
    hdnSchema: many(hdnMessageSchema),
    hdnReceiverBrondataleverancier: many(hdnReceiverBrondataleverancier),
    hdnReceiverFeature: many(hdnReceiverFeature),
}));
export const hdnProvider = pgTable("hdn_provider", {
    name: text("name").notNull().primaryKey(),
    mortgage: boolean("mortgage").default(false).notNull(),
    insurance: boolean("insurance").default(false).notNull(),
    credit: boolean("credit").default(false).notNull(),
    receiverCode: text("receiver_code")
        .references(() => hdnReceiver.receiverCode, {
        onDelete: "set null",
        onUpdate: "cascade",
    })
        .$type(),
    logo: text("logo"),
    icon: text("icon"),
    label: text("label"),
    isActive: boolean("is_active").default(true).notNull(),
    createdAt: timestamp("created_at", {
        withTimezone: true,
        mode: "string",
    })
        .defaultNow()
        .notNull(),
});
export const hdnProviderRelations = relations(hdnProvider, ({ one, many }) => ({
    hdnReceiver: one(hdnReceiver, {
        fields: [hdnProvider.receiverCode],
        references: [hdnReceiver.receiverCode],
    }),
    hdnProducts: many(hdnProviderProduct),
    hdnPartProducts: many(hdnProviderProductPart),
    serviceProvidersToProviders: many(hdnServiceProviderToProvider),
    connections: many(hdnLenderConnection),
}));
export const hdnProviderProduct = pgTable("hdn_provider_product", {
    providerCode: text("provider_code")
        .notNull()
        .references(() => hdnProvider.name, {
        onDelete: "cascade",
        onUpdate: "cascade",
    }),
    productCode: text("product_code").notNull().unique(),
    isActive: boolean("is_active").default(true).notNull(),
    id: uuid("id")
        .default(sql `uuid_generate_v4()`)
        .primaryKey()
        .notNull(),
    createdAt: timestamp("created_at", {
        withTimezone: true,
        mode: "string",
    })
        .defaultNow()
        .notNull(),
    updatedAt: timestamp("updated_at", {
        withTimezone: true,
        mode: "string",
    })
        .defaultNow()
        .notNull(),
});
export const hdnProviderProductRelations = relations(hdnProviderProduct, ({ one }) => ({
    hdnProvider: one(hdnProvider, {
        fields: [hdnProviderProduct.providerCode],
        references: [hdnProvider.name],
    }),
}));
export const hdnProviderProductPart = pgTable("hdn_provider_product_part", {
    providerCode: text("provider_code")
        .notNull()
        .references(() => hdnProvider.name, {
        onDelete: "cascade",
        onUpdate: "cascade",
    }),
    productPartCode: text("product_part_code").notNull(),
    isActive: boolean("is_active").default(true).notNull(),
    id: uuid("id")
        .default(sql `uuid_generate_v4()`)
        .primaryKey()
        .notNull(),
    createdAt: timestamp("created_at", {
        withTimezone: true,
        mode: "string",
    })
        .defaultNow()
        .notNull(),
    updatedAt: timestamp("updated_at", {
        withTimezone: true,
        mode: "string",
    })
        .defaultNow()
        .notNull(),
}, (table) => [
    unique("hdn_provider_product_part_provider_code_product_part_code_key").on(table.providerCode, table.productPartCode),
]);
export const hdnProviderProductPartRelations = relations(hdnProviderProductPart, ({ one }) => ({
    hdnProvider: one(hdnProvider, {
        fields: [hdnProviderProductPart.providerCode],
        references: [hdnProvider.name],
    }),
}));
export const hdnReceiverBrondataleverancier = pgTable("hdn_receiver_brondataleverancier", {
    receiverId: text("receiver_id").notNull(),
    brondataleverancier: text("brondataleverancier")
        .notNull()
        .$type(),
    isSupported: boolean("is_supported"),
    updatedAt: timestamp("updated_at", {
        withTimezone: true,
        mode: "string",
    })
        .defaultNow()
        .notNull(),
    createdAt: timestamp("created_at", {
        withTimezone: true,
        mode: "string",
    })
        .defaultNow()
        .notNull(),
    id: uuid("id")
        .default(sql `uuid_generate_v4()`)
        .primaryKey()
        .notNull(),
}, (table) => [
    unique("unique_provider_brondataleverancier").on(table.receiverId, table.brondataleverancier),
]);
export const hdnReceiverBrondataleverancierRelations = relations(hdnReceiverBrondataleverancier, ({ one }) => ({
    hdnReceiver: one(hdnReceiver, {
        fields: [hdnReceiverBrondataleverancier.receiverId],
        references: [hdnReceiver.receiverId],
    }),
}));
export const hdnReceiverFeature = pgTable("hdn_receiver_feature", {
    id: uuid("id")
        .default(sql `uuid_generate_v4()`)
        .primaryKey()
        .notNull(),
    receiver: text("receiver").notNull(),
    feature: text("feature").notNull(),
    value: jsonb("value"),
    createdAt: timestamp("created_at", {
        withTimezone: true,
        mode: "string",
    })
        .defaultNow()
        .notNull(),
    updatedAt: timestamp("updated_at", {
        withTimezone: true,
        mode: "string",
    })
        .defaultNow()
        .notNull(),
}, (table) => [
    unique("unique_receiver_feature").on(table.receiver, table.feature),
]);
export const hdnReceiverFeatureRelations = relations(hdnReceiverFeature, ({ one }) => ({
    hdnReceiver: one(hdnReceiver, {
        fields: [hdnReceiverFeature.receiver],
        references: [hdnReceiver.receiverId],
    }),
}));
export const hdnServiceProvider = pgTable("hdn_service_provider", {
    name: text("name"),
    logo: text("logo"),
    icon: text("icon"),
    nodeNumber: text("node_number")
        .notNull()
        .references(() => hdnNode.nodeNumber, { onUpdate: "cascade" }),
    createdAt: timestamp("created_at", {
        withTimezone: true,
        mode: "string",
    })
        .defaultNow()
        .notNull(),
    id: uuid("id")
        .default(sql `uuid_generate_v4()`)
        .primaryKey()
        .notNull(),
});
export const hdnServiceProviderRelations = relations(hdnServiceProvider, ({ one, many }) => ({
    hdnNode: one(hdnNode, {
        fields: [hdnServiceProvider.nodeNumber],
        references: [hdnNode.nodeNumber],
    }),
    connections: many(agencyServiceProviderConnection),
    serviceProvidersToProviders: many(hdnServiceProviderToProvider),
}));
export const hdnServiceProviderToProvider = pgTable("hdn_service_provider_to_provider", {
    serviceProviderId: uuid("service_provider_id")
        .notNull()
        .references(() => hdnServiceProvider.id, {
        onDelete: "cascade",
        onUpdate: "cascade",
    }),
    providerCode: text("provider_code")
        .notNull()
        .references(() => hdnProvider.name, {
        onDelete: "cascade",
        onUpdate: "cascade",
    }),
    createdAt: timestamp("created_at", {
        withTimezone: true,
        mode: "string",
    })
        .defaultNow()
        .notNull(),
}, (table) => [
    primaryKey({
        name: "unique_service_provider_receiver_pkey",
        columns: [table.serviceProviderId, table.providerCode],
    }),
]);
export const hdnServiceProviderToProviderRelations = relations(hdnServiceProviderToProvider, ({ one }) => ({
    serviceProvider: one(hdnServiceProvider, {
        fields: [hdnServiceProviderToProvider.serviceProviderId],
        references: [hdnServiceProvider.id],
    }),
    provider: one(hdnProvider, {
        fields: [hdnServiceProviderToProvider.providerCode],
        references: [hdnProvider.name],
    }),
}));
export const hdnLenderConnection = pgTable("hdn_lender_connection", {
    agencyId: uuid("agency_id")
        .notNull()
        .references(() => agency.id, {
        onDelete: "cascade",
        onUpdate: "cascade",
    }),
    lenderCode: text("lender_code").references(() => hdnProvider.name, {
        onDelete: "cascade",
        onUpdate: "cascade",
    }),
    serviceProviderId: uuid("service_provider_id").references(() => hdnServiceProvider.id, {
        onDelete: "cascade",
        onUpdate: "cascade",
    }),
    connectionNumber: text("connection_number"),
    sendMethod: text("send_method").$type(),
    activeSince: timestamp("active_since", {
        withTimezone: true,
        mode: "string",
    }).defaultNow(),
    createdAt: timestamp("created_at", {
        withTimezone: true,
        mode: "string",
    })
        .defaultNow()
        .notNull(),
}, (table) => [
    primaryKey({
        name: "hdn_lender_connection_pkey",
        columns: [table.agencyId, table.lenderCode],
    }),
]);
export const hdnLenderConnectionRelations = relations(hdnLenderConnection, ({ one }) => ({
    lender: one(hdnProvider, {
        fields: [hdnLenderConnection.lenderCode],
        references: [hdnProvider.name],
    }),
    agency: one(agency, {
        fields: [hdnLenderConnection.agencyId],
        references: [agency.id],
    }),
    serviceProvider: one(hdnServiceProvider, {
        fields: [hdnLenderConnection.serviceProviderId],
        references: [hdnServiceProvider.id],
    }),
}));
export const hdnPartijNawData = pgTable("hdn_partij_naw_data", {
    content: jsonb("content").notNull(),
    givenNames: text("given_names"),
    birthInsert: text("birth_insert"),
    birthSurname: text("birth_surname"),
    correspondenceFirstname: text("correspondence_firstname"),
    correspondenceInsert: text("correspondence_insert"),
    correspondenceSurname: text("correspondece_surname"),
    initials: text("initials"),
    birthDate: text("birth_date"),
    createdAt: timestamp("created_at", {
        withTimezone: true,
        mode: "string",
    })
        .defaultNow()
        .notNull(),
    hdnRef: text("hdn_ref").notNull(),
    hdnDossierId: uuid("hdn_dossier_id")
        .notNull()
        .references(() => hdnDossierTable.id, {
        onDelete: "cascade",
        onUpdate: "cascade",
    }),
    hdnLoanApplicationId: uuid("hdn_loan_application_id").references(() => hdnLoanApplication.id, {
        onDelete: "cascade",
        onUpdate: "cascade",
    }),
    agencyId: uuid("agency_id")
        .notNull()
        .references(() => agency.id, {
        onDelete: "cascade",
        onUpdate: "cascade",
    }),
    id: uuid("id")
        .default(sql `uuid_generate_v4()`)
        .primaryKey()
        .notNull(),
}, (table) => [
    unique("hdn_partij_naw_data_hdn_ref_hdn_loan_application_id_key").on(table.hdnRef, table.hdnLoanApplicationId),
]);
export const hdnPartijNawDataRelations = relations(hdnPartijNawData, ({ one, many }) => ({
    hdnDossier: one(hdnDossierTable, {
        fields: [hdnPartijNawData.hdnDossierId],
        references: [hdnDossierTable.id],
    }),
    loanApplication: one(hdnLoanApplication, {
        fields: [hdnPartijNawData.hdnLoanApplicationId],
        references: [hdnLoanApplication.id],
    }),
    agency: one(agency, {
        fields: [hdnPartijNawData.agencyId],
        references: [agency.id],
    }),
    requestedDocuments: many(hdnRequestedDocument),
}));
export const hdnRequestedDocument = pgTable("hdn_requested_document", {
    content: jsonb("content").notNull().$type(),
    documentType: text("document_type").notNull(),
    documentTypeDescription: text("document_type_description"),
    documentMijNr: text("document_mij_nr").notNull(),
    submissionMoment: text("submission_moment"),
    opgevraagdViaBron: boolean("opgevraagd_via_bron").notNull(),
    partijNawDataId: uuid("partij_naw_data_id").references(() => hdnPartijNawData.id, { onDelete: "set null" }),
    linkedDocumentActionId: uuid("linked_document_id").references(() => hdnRequestedDocumentAction.id, {
        onDelete: "set null",
    }),
    sentDocumentActionId: uuid("sent_document_id").references(() => hdnRequestedDocumentAction.id, {
        onDelete: "set null",
    }),
    sentDocumentAt: timestamp("sent_document_at", {
        withTimezone: true,
        mode: "string",
    }),
    statusmelding: text("statusmelding"),
    statusmeldingDescription: text("statusmelding_description"),
    statusmeldingDatum: timestamp("statusmelding_datum", {
        withTimezone: true,
        mode: "string",
    }),
    documentStatusId: uuid("document_status_id").references(() => hdnRequestedDocumentStatusUpdate.id, {
        onDelete: "set null",
    }),
    ketenpartijStatusId: uuid("ketenpartij_status_id").references(() => hdnRequestedDocumentStatusUpdate.id, {
        onDelete: "set null",
    }),
    createdAt: timestamp("created_at", {
        withTimezone: true,
        mode: "string",
    })
        .defaultNow()
        .notNull(),
    hdnLoanApplicationId: uuid("hdn_loan_application_id")
        .notNull()
        .references(() => hdnLoanApplication.id, {
        onDelete: "cascade",
        onUpdate: "cascade",
    }),
    agencyId: uuid("agency_id")
        .notNull()
        .references(() => agency.id, {
        onDelete: "cascade",
        onUpdate: "cascade",
    }),
    id: uuid("id")
        .default(sql `uuid_generate_v4()`)
        .primaryKey()
        .notNull(),
}, (table) => [
    unique("hdn_requested_document_document_mij_nr_key").on(table.documentMijNr, table.hdnLoanApplicationId),
]);
export const hdnRequestedDocumentRelations = relations(hdnRequestedDocument, ({ one, many }) => ({
    linkedDocumentAction: one(hdnRequestedDocumentAction, {
        fields: [hdnRequestedDocument.linkedDocumentActionId],
        references: [hdnRequestedDocumentAction.id],
    }),
    sentDocumentAction: one(hdnRequestedDocumentAction, {
        fields: [hdnRequestedDocument.sentDocumentActionId],
        references: [hdnRequestedDocumentAction.id],
    }),
    documentStatus: one(hdnRequestedDocumentStatusUpdate, {
        fields: [hdnRequestedDocument.documentStatusId],
        references: [hdnRequestedDocumentStatusUpdate.id],
    }),
    ketenpartijStatus: one(hdnRequestedDocumentStatusUpdate, {
        fields: [hdnRequestedDocument.ketenpartijStatusId],
        references: [hdnRequestedDocumentStatusUpdate.id],
    }),
    partijNawData: one(hdnPartijNawData, {
        fields: [hdnRequestedDocument.partijNawDataId],
        references: [hdnPartijNawData.id],
    }),
    updates: many(hdnRequestedDocumentStatusUpdate),
    actions: many(hdnRequestedDocumentAction),
    loanApplication: one(hdnLoanApplication, {
        fields: [hdnRequestedDocument.hdnLoanApplicationId],
        references: [hdnLoanApplication.id],
    }),
    agency: one(agency, {
        fields: [hdnRequestedDocument.agencyId],
        references: [agency.id],
    }),
}));
export const hdnRequestedDocumentAction = pgTable("hdn_requested_document_action", {
    changedBy: uuid("changed_by")
        .notNull()
        .references(() => appUser.id, { onUpdate: "cascade" }),
    changedAt: timestamp("changedAt", {
        withTimezone: true,
        mode: "string",
    })
        .notNull()
        .defaultNow(),
    documentId: uuid("user_document_id").references(() => dossierDocument.id, {
        onDelete: "set null",
        onUpdate: "cascade",
    }),
    actionType: text("action_type").notNull().$type(),
    requestedDocumentId: uuid("requested_document_id")
        .references(() => hdnRequestedDocument.id, {
        onDelete: "cascade",
        onUpdate: "cascade",
    })
        .notNull(),
    agencyId: uuid("agency_id")
        .notNull()
        .references(() => agency.id, {
        onDelete: "cascade",
        onUpdate: "cascade",
    }),
    id: uuid("id")
        .default(sql `uuid_generate_v4()`)
        .primaryKey()
        .notNull(),
    createdAt: timestamp("created_at", {
        withTimezone: true,
        mode: "string",
    })
        .defaultNow()
        .notNull(),
});
export const hdnRequestedDocumentActionRelations = relations(hdnRequestedDocumentAction, ({ one }) => ({
    agency: one(agency, {
        fields: [hdnRequestedDocumentAction.agencyId],
        references: [agency.id],
    }),
    requestedDocument: one(hdnRequestedDocument, {
        fields: [hdnRequestedDocumentAction.requestedDocumentId],
        references: [hdnRequestedDocument.id],
    }),
    document: one(dossierDocument, {
        fields: [hdnRequestedDocumentAction.documentId],
        references: [dossierDocument.id],
    }),
}));
export const hdnRequestedDocumentStatusUpdate = pgTable("hdn_requested_document_status_update", {
    status: text("status").notNull(),
    description: text("description"),
    source: text("source")
        .notNull()
        .default("default")
        .$type(),
    hdnRequestedDocumentId: uuid("hdn_requested_document_id")
        .notNull()
        .references(() => hdnRequestedDocument.id, {
        onDelete: "cascade",
        onUpdate: "cascade",
    }),
    agencyId: uuid("agency_id")
        .notNull()
        .references(() => agency.id, {
        onDelete: "cascade",
        onUpdate: "cascade",
    }),
    updatedInMessageId: uuid("updated_in_message_id")
        .notNull()
        .references(() => hdnMessage.id, {
        onUpdate: "cascade",
        onDelete: "cascade",
    }),
    createdAt: timestamp("created_at", {
        withTimezone: true,
        mode: "string",
    })
        .defaultNow()
        .notNull(),
    id: uuid("id")
        .default(sql `uuid_generate_v4()`)
        .primaryKey()
        .notNull(),
});
export const hdnRequestedDocumentUpdateRelations = relations(hdnRequestedDocumentStatusUpdate, ({ one }) => ({
    hdnMessage: one(hdnMessage, {
        fields: [hdnRequestedDocumentStatusUpdate.updatedInMessageId],
        references: [hdnMessage.id],
    }),
    agency: one(agency, {
        fields: [hdnRequestedDocumentStatusUpdate.agencyId],
        references: [agency.id],
    }),
    requestedDocument: one(hdnRequestedDocument, {
        fields: [hdnRequestedDocumentStatusUpdate.hdnRequestedDocumentId],
        references: [hdnRequestedDocument.id],
    }),
}));
export const hdnPublicKeyTable = pgTable("hdn_public_key", {
    nodeNumber: text("node_number")
        .notNull()
        .references(() => hdnNode.nodeNumber, { onUpdate: "cascade" }),
    hdnId: uuid("hdn_id"),
    azureKeyName: text("azure_key_name").notNull(),
    agencyId: uuid("agency_id")
        .notNull()
        .references(() => agency.id, {
        onDelete: "cascade",
        onUpdate: "cascade",
    }),
    createdAt: timestamp("created_at", {
        withTimezone: true,
        mode: "string",
    })
        .defaultNow()
        .notNull(),
    id: uuid("id")
        .default(sql `uuid_generate_v4()`)
        .primaryKey()
        .notNull(),
});
export const hdnPublicKeyRelations = relations(hdnPublicKeyTable, ({ one }) => ({
    hdnNode: one(hdnNode, {
        fields: [hdnPublicKeyTable.nodeNumber],
        references: [hdnNode.nodeNumber],
    }),
    agency: one(agency, {
        fields: [hdnPublicKeyTable.agencyId],
        references: [agency.id],
    }),
}));
